import { KpiCounts } from "../derive";
import Measured from "./Measured";

interface iSummaryBandProps {
    counts: KpiCounts;
}

type Tone = "bad" | "warn" | "ok" | "mute";

interface iTile {
    label: string;
    value?: number;
    tone: Tone;
    /**
     * Coverage note, rendered small beneath the value.
     *
     * Only the emails-given-up tile carries one. Every other tile counts
     * tenants out of a known roster, so its denominator is not in doubt. That
     * one sums an item count across tenants, and some tenants may not have been
     * measured at all — showing the bare total would present a partial sum as
     * complete, which is the thing `MeasuredTotal.measured` exists to prevent.
     */
    note?: string;
    /**
     * Overrides `Measured`'s default absent title ("this tenant could not be
     * read") for a tile whose value is a tenant-spanning aggregate, not one
     * tenant's own reading. Only meaningful — and only applied by `Measured`
     * — while `value` is actually absent; a defined value renders no title.
     */
    absentTitle?: string;
}

/**
 * The six summary tiles.
 *
 * THREE OF THEM PARTITION THE ROSTER — Not healthy, Healthy and
 * Disabled / never sum to the tenant total. The other three are detail: Stale
 * and Tenants with stalled queues are breakdowns that overlap Not healthy, and
 * Emails given up counts a different unit entirely.
 *
 * Not healthy and Healthy read the same standing the table's dot shows, so the
 * band and the table cannot disagree. They used to: both counted
 * `collectionHealth`, so a tenant whose collector was fine and whose mailboxes
 * were all broken was counted "Healthy" directly above its own red row.
 *
 * Tone is semantic, not decorative. The inactive tile is deliberately muted:
 * Disabled is a configuration statement, and alarming on it trains operators
 * to ignore the band.
 *
 * Five tiles count TENANTS. One — emails given up — counts ITEMS summed across
 * tenants, so it is labelled with its unit and carries a coverage note. Without
 * that, "Given up 157" sits beside "Failing 2" in an identical tile and reads
 * as 157 tenants.
 *
 * A bad/warn tile whose value is a MEASURED zero calms its chrome (border and
 * background) to the neutral tile style: on a clean refresh these tiles are
 * usually zero, and fault-coloured chrome that fires on nearly every load is
 * how an operator learns to stop reading the band's colour at all — the same
 * reasoning that keeps the inactive tile muted. This does not make a measured
 * zero and an absent value look the same: `Measured` renders them in the same
 * grey (`.mp-zero` and `.mp-absent` are both #bbbbbb, and `.mp-zero` is a
 * direct declaration that wins regardless of the tile's tone), but they stay
 * distinguishable by glyph (`0` vs `—`), by the dotted underline that only
 * `.mp-absent` carries, and by this very chrome — `mp-kpi-calm` fires for a
 * measured zero and never for an absent value, per the check below.
 *
 * The calm check is strictly `=== 0`, never a falsy check. `undefined` (not
 * measured) must NOT calm the chrome: doing so would present an unmeasured
 * total as good news, which is the absent-vs-zero failure this whole feature
 * exists to prevent, recurring one layer up in the tile chrome.
 */
function SummaryBand({ counts }: iSummaryBandProps) {
    // Six tiles, not seven. The Behind tile is deliberately absent while the
    // direction verdict is deferred to Spec 2 — it could only ever render an
    // em dash this release, which beside a table full of sparklines reads as a
    // fault rather than as "not yet measured". Spec 2 restores it.
    const tiles: iTile[] = [
        { label: "Not healthy", value: counts.notHealthy, tone: "bad" },
        { label: "Stale", value: counts.stale, tone: "warn" },
        // "Tenants with", because this counts TENANTS that have at least one
        // stalled queue, not queues. A tenant with three stalled queues adds 1.
        // As "Stalled queues" the label and the number agreed only by accident,
        // whenever exactly one tenant had exactly one stalled queue.
        { label: "Tenants with stalled queues", value: counts.stalledQueues, tone: "warn" },
        {
            label: "Emails given up",
            value: counts.givenUp,
            tone: "bad",
            note: `across ${counts.measuredGivenUp} of ${counts.total} tenants`,
            // Sums across every tenant; absent means none of them reported a
            // measurable terminal block, not that one tenant was unreadable.
            absentTitle: "Not measured — no tenant reported a measurable terminal block",
        },
        { label: "Healthy", value: counts.healthy, tone: "ok" },
        { label: "Disabled / never", value: counts.inactive, tone: "mute" },
    ];

    return (
        <div className="mp-band">
            {tiles.map(tile => (
                <div
                    key={tile.label}
                    className={`mp-kpi mp-kpi-${tile.tone}${tile.value === 0 ? " mp-kpi-calm" : ""}`}
                >
                    <div className="mp-kpi-value">
                        <Measured
                            value={tile.value}
                            title={tile.value === undefined ? tile.absentTitle : undefined}
                        />
                    </div>
                    <div className="mp-kpi-label">{tile.label}</div>
                    {tile.note && <div className="mp-kpi-note">{tile.note}</div>}
                </div>
            ))}
        </div>
    );
}

export default SummaryBand;
